import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency, daysAgo, csvCell } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, ChevronRight, ChevronLeft, Phone, Mail, Upload, MessageSquare, BarChart3, Download, Link2, Send, X, RefreshCw, AlertTriangle } from "lucide-react";

const STAGES = [
  { id: "new_lead", label: "New Lead", color: "border-t-slate-400" },
  { id: "contacted", label: "Contacted", color: "border-t-blue-400" },
  { id: "consult_scheduled", label: "Consult Scheduled", color: "border-t-cyan-500" },
  { id: "medical_evaluation", label: "Medical Eval", color: "border-t-violet-500" },
  { id: "converted", label: "Converted", color: "border-t-emerald-500" },
  { id: "lost", label: "Lost", color: "border-t-red-400" },
];
const SERVICES = ["TRT (Testosterone)", "Weight Loss / GLP-1", "Hair Restoration", "ED Treatment",
  "Peptide Therapy", "Hormone Optimization", "Sexual Wellness", "Longevity / Anti-Aging"];
const SOURCES = ["Google Ads", "Instagram", "Physician Referral", "Patient Word-of-Mouth",
  "Local Gym Partner", "Website Organic", "Facebook", "Podcast Sponsorship"];
const RANGES = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all", label: "All" },
];

function scoreColor(s) {
  if (s >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s >= 60) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function LeadCard({ lead, onMove, onWelcome }) {
  const idx = STAGES.findIndex((s) => s.id === lead.stage);
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
      data-testid={`lead-card-${lead.id}`}
      className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm text-slate-900 leading-tight">{lead.name}</div>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${scoreColor(lead.lead_score)}`}>{lead.lead_score}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1">
        <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">
          {lead.service_interest}
        </span>
        {lead.inquiry_count > 1 && (
          <span data-testid={`lead-returning-${lead.id}`} className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            Returning ×{lead.inquiry_count}
          </span>
        )}
      </div>
      {lead.last_inquiry_at && (
        <div data-testid={`lead-last-submitted-${lead.id}`} className="mt-1 text-[11px] text-amber-700">
          Resubmitted {new Date(lead.last_inquiry_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
        <span className="font-mono-metric text-slate-700 font-semibold">{fmtCurrency(lead.target_value)}</span>
        <span>{daysAgo(lead.last_inquiry_at || lead.last_contacted)}</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-400">{lead.source}</div>
      {lead.stage === "new_lead" && lead.phone && (
        <button data-testid={`lead-welcome-${lead.id}`} onClick={() => onWelcome(lead)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-md py-1.5 transition-colors">
          <MessageSquare className="h-3.5 w-3.5" /> Send welcome text
        </button>
      )}
      <div className="mt-2.5 flex items-center justify-between border-t border-slate-100 pt-2">
        <button data-testid={`lead-prev-${lead.id}`} disabled={idx <= 0}
          onClick={() => onMove(lead, STAGES[idx - 1].id)}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30 transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex gap-2 text-slate-300">
          <Phone className="h-3.5 w-3.5" /><Mail className="h-3.5 w-3.5" />
        </div>
        <button data-testid={`lead-next-${lead.id}`} disabled={idx >= STAGES.length - 1}
          onClick={() => onMove(lead, STAGES[idx + 1].id)}
          className="text-slate-400 hover:text-cyan-600 disabled:opacity-30 transition-colors">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

export default function LeadPipeline() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: SERVICES[0], source: SOURCES[0], target_value: "", lead_score: 60 });

  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });

  const move = useMutation({
    mutationFn: async ({ lead, stage }) => (await api.put(`/leads/${lead.id}/stage`, { stage })).data,
    onMutate: async ({ lead, stage }) => {
      await qc.cancelQueries({ queryKey: ["leads"] });
      const prev = qc.getQueryData(["leads"]);
      qc.setQueryData(["leads"], (old = []) => old.map((l) => (l.id === lead.id ? { ...l, stage } : l)));
      return { prev };
    },
    onError: (e, v, ctx) => qc.setQueryData(["leads"], ctx.prev),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["funnel"] }); },
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/leads", { ...form, target_value: Number(form.target_value) || 0, lead_score: Number(form.lead_score) })).data,
    onSuccess: () => {
      toast.success("Lead added to pipeline");
      qc.invalidateQueries({ queryKey: ["leads"] });
      setOpen(false);
      setForm({ name: "", email: "", phone: "", service_interest: SERVICES[0], source: SOURCES[0], target_value: "", lead_score: 60 });
    },
  });

  const fileRef = useRef(null);
  const importLeads = useMutation({
    mutationFn: async (file) => { const fd = new FormData(); fd.append("file", file); return (await api.post("/leads/import", fd)).data; },
    onSuccess: (d) => {
      toast.success(`Imported ${d.imported} lead${d.imported === 1 ? "" : "s"}${d.skipped ? ` · ${d.skipped} duplicate${d.skipped === 1 ? "" : "s"} skipped` : ""}`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["funnel"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Import failed"),
  });

  const welcome = useMutation({
    mutationFn: async (lead) => (await api.post(`/leads/${lead.id}/welcome-sms`)).data,
    onSuccess: (d) => {
      toast.success(
        d.status === "opted_out" ? "Lead has opted out of texts"
          : d.status === "simulated" ? "Welcome text queued (SMS not configured)"
          : d.status === "sent" ? "Welcome text sent · moved to Contacted"
          : "Could not text the lead");
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["funnel"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not text the lead"),
  });

  const { data: smsSettings } = useQuery({ queryKey: ["sms-settings"], queryFn: async () => (await api.get("/settings/sms")).data });
  const saveBooking = useMutation({
    mutationFn: async (url) => (await api.post("/settings/sms", { ...smsSettings, booking_url: url, auto_replies: smsSettings?.auto_replies || [] })).data,
    onSuccess: () => { toast.success("Booking link saved"); setBookingOpen(false); qc.invalidateQueries({ queryKey: ["sms-settings"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save"),
  });
  const welcomeAll = useMutation({
    mutationFn: async (ids) => (await api.post("/leads/welcome-all", { lead_ids: ids })).data,
    onSuccess: (d) => {
      toast.success(`Welcome texts: ${d.sent} sent${d.opted_out ? ` · ${d.opted_out} opted out` : ""}${d.skipped ? ` · ${d.skipped} skipped` : ""}`);
      setWelcomeAllOpen(false);
      qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["funnel"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Bulk text failed"),
  });

  const { data: syncCfg } = useQuery({ queryKey: ["lead-sync"], queryFn: async () => (await api.get("/leads/sync-settings")).data });
  const saveSync = useMutation({
    mutationFn: async () => (await api.post("/leads/sync-settings", { url: syncUrl.trim(), enabled: syncEnabled })).data,
    onSuccess: () => { toast.success("Auto-sync settings saved"); qc.invalidateQueries({ queryKey: ["lead-sync"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save"),
  });
  const syncNow = useMutation({
    mutationFn: async (opts) => {
      if (!opts?.retryOnly) await api.post("/leads/sync-settings", { url: syncUrl.trim(), enabled: syncEnabled });
      return (await api.post("/leads/sync-now")).data;
    },
    onSuccess: (d) => {
      if (d.status === "ok") toast.success(`Synced · imported ${d.imported}${d.returning ? `, ${d.returning} returning` : ""}, ${d.skipped} duplicate${d.skipped === 1 ? "" : "s"} skipped`);
      else if (d.status === "no-source") toast.error("Add a data source URL first");
      else if (d.status === "error") toast.error(`Sync failed: ${d.error || ""}`);
      else toast.info("Auto-sync is turned off");
      qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["lead-sync"] });
      qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["funnel"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Sync failed"),
  });

  const [range, setRange] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(() => new URLSearchParams(window.location.search).get("source"));
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [welcomeAllOpen, setWelcomeAllOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncUrl, setSyncUrl] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const inRange = (iso) => {
    if (range === "all") return true;
    if (!iso) return false;
    const d = new Date(iso); const now = new Date();
    if (range === "today") return d.toDateString() === now.toDateString();
    if (range === "week") { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); s.setHours(0, 0, 0, 0); return d >= s; }
    if (range === "month") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    return true;
  };
  const filtered = leads.filter((l) => inRange(l.last_inquiry_at || l.created_at));
  const rangeLabel = { today: "Today", week: "This week", month: "This month", all: "All time" }[range];
  const bySource = {};
  filtered.forEach((l) => {
    const s = l.source || "Unknown";
    (bySource[s] = bySource[s] || { source: s, count: 0, converted: 0, value: 0 });
    bySource[s].count += 1;
    if (l.stage === "converted") { bySource[s].converted += 1; bySource[s].value += l.target_value || 0; }
  });
  const sources = Object.values(bySource).sort((a, b) => b.count - a.count);
  const maxCount = Math.max(1, ...sources.map((s) => s.count));
  const activity = (l) => l.last_inquiry_at || l.created_at || "";
  const boardLeads = (sourceFilter ? filtered.filter((l) => (l.source || "Unknown") === sourceFilter) : filtered)
    .slice().sort((a, b) => (new Date(activity(b)) - new Date(activity(a))) || 0);
  const newToText = boardLeads.filter((l) => l.stage === "new_lead" && l.phone);
  const exportSourceCSV = () => {
    const rows = [["Source", "Leads", "Converted", "Converted Revenue"], ...sources.map((s) => [s.source, s.count, s.converted, s.value])];
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `leads-by-source-${range}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Layout title="Lead Pipeline" subtitle="Drag prospects through to converted patients"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mr-1" data-testid="lead-range-toggle">
            {RANGES.map((r) => (
              <button key={r.id} data-testid={`range-${r.id}`} onClick={() => setRange(r.id)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${range === r.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" data-testid="booking-link-button" onClick={() => { setBookingUrl(smsSettings?.booking_url || ""); setBookingOpen(true); }} className="border-slate-300">
            <Link2 className="h-4 w-4 mr-2" /> Booking link
          </Button>
          <Button variant="outline" size="sm" data-testid="welcome-all-button" onClick={() => setWelcomeAllOpen(true)} disabled={newToText.length === 0} className="border-slate-300">
            <Send className="h-4 w-4 mr-2" /> Text new ({newToText.length})
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="lead-import-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importLeads.mutate(f); e.target.value = ""; }} />
          <Button variant="outline" data-testid="import-leads-button" onClick={() => fileRef.current?.click()} disabled={importLeads.isPending} className="border-slate-300">
            <Upload className={`h-4 w-4 mr-2 ${importLeads.isPending ? "animate-pulse" : ""}`} /> Import CSV
          </Button>
          <Button variant="outline" data-testid="lead-sync-button" onClick={() => { setSyncUrl(syncCfg?.url || ""); setSyncEnabled(syncCfg?.enabled ?? true); setSyncOpen(true); }} className="border-slate-300">
            <RefreshCw className="h-4 w-4 mr-2" /> Auto-sync
          </Button>
          <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
            <DialogContent className="sm:max-w-lg" data-testid="lead-sync-dialog">
              <DialogHeader><DialogTitle className="font-heading">Hourly lead auto-sync</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Paste a Google Sheet published as CSV (in the sheet: <span className="font-medium">File → Share → Publish to web → pick the tab → CSV</span>).
                  New leads import automatically every hour. If an existing lead submits again, their card shows a Returning badge and appears under Today; their stage and notes are never overwritten.
                </p>
                <div className="space-y-1.5"><Label>Data source URL (CSV)</Label>
                  <Input data-testid="lead-sync-url-input" value={syncUrl} onChange={(e) => setSyncUrl(e.target.value)}
                    placeholder="https://docs.google.com/.../pub?output=csv" /></div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="lead-sync-enabled" checked={syncEnabled} onCheckedChange={setSyncEnabled} />
                  <span className="text-sm text-slate-600">Hourly auto-sync enabled</span>
                </div>
                {syncCfg?.last_run && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-2.5" data-testid="lead-sync-status">
                    Last sync {daysAgo(syncCfg.last_run)} — {syncCfg.last_result?.status === "ok"
                      ? `imported ${syncCfg.last_result.imported}${syncCfg.last_result.returning ? `, returning ${syncCfg.last_result.returning}` : ""}, skipped ${syncCfg.last_result.skipped}`
                      : (syncCfg.last_result?.status === "no-source" ? "no source URL set"
                        : syncCfg.last_result?.status === "error" ? `error: ${syncCfg.last_result.error || ""}`
                        : syncCfg.last_result?.status || "—")}
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="lead-sync-now-button" onClick={() => syncNow.mutate()} disabled={syncNow.isPending || !syncUrl.trim()}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncNow.isPending ? "animate-spin" : ""}`} /> Sync now
                </Button>
                <Button data-testid="lead-sync-save-button" onClick={() => saveSync.mutate()} disabled={saveSync.isPending} className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-lead-button" className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> New Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Add New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label>Full name</Label>
                <Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Carter" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input data-testid="lead-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@email.com" /></div>
                <div className="space-y-1.5"><Label>Phone</Label>
                  <Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1…" /></div>
              </div>
              <div className="space-y-1.5"><Label>Service interest</Label>
                <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                  <SelectTrigger data-testid="lead-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Target value ($)</Label>
                  <Input data-testid="lead-value-input" type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} placeholder="5000" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="lead-save-button" disabled={!form.name || create.isPending}
                onClick={() => create.mutate()} className="bg-slate-900 hover:bg-slate-800 text-white w-full">
                {create.isPending ? "Saving…" : "Add to Pipeline"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      {syncCfg && !(syncCfg.url || "").trim() && (
        <div data-testid="lead-sync-missing-warning" role="alert"
          className="flex flex-wrap items-center justify-between gap-3 mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-3">
          <div className="flex items-center gap-2.5 text-red-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">No sheet link saved</div>
              <div className="text-xs text-red-600">Auto-sync has no Google Sheet to pull from, so new leads won't arrive. Add the published sheet link to resume hourly imports.</div>
            </div>
          </div>
          <Button data-testid="lead-sync-missing-fix-button" size="sm" className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => { setSyncUrl(""); setSyncEnabled(syncCfg?.enabled ?? true); setSyncOpen(true); }}>
            Add sheet link
          </Button>
        </div>
      )}
      {syncCfg && (syncCfg.url || "").trim() && syncCfg.last_result?.status === "error" && (
        <div data-testid="lead-sync-error-warning" role="alert"
          className="flex flex-wrap items-center justify-between gap-3 mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-3">
          <div className="flex items-center gap-2.5 text-red-700 min-w-0">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm">Last lead sync failed{syncCfg.last_run ? ` · ${daysAgo(syncCfg.last_run)}` : ""}</div>
              <div data-testid="lead-sync-error-text" className="text-xs text-red-600 break-words">{syncCfg.last_result.error || "Unknown error"}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="lead-sync-error-settings-button" size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => { setSyncUrl(syncCfg.url || ""); setSyncEnabled(syncCfg.enabled ?? true); setSyncOpen(true); }}>
              Check link
            </Button>
            <Button data-testid="lead-sync-error-retry-button" size="sm" className="bg-red-600 hover:bg-red-700 text-white"
              disabled={syncNow.isPending} onClick={() => syncNow.mutate({ retryOnly: true })}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncNow.isPending ? "animate-spin" : ""}`} />{syncNow.isPending ? "Retrying…" : "Retry"}
            </Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6" data-testid="lead-source-report">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-cyan-600" /> Leads by Source</h3>
          <div className="flex items-center gap-3">
            {sourceFilter && (
              <button data-testid="clear-source-filter" onClick={() => setSourceFilter(null)} className="text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1 flex items-center gap-1">
                {sourceFilter} <X className="h-3 w-3" />
              </button>
            )}
            <span className="text-xs text-slate-400">{filtered.length} lead{filtered.length === 1 ? "" : "s"} · {rangeLabel}</span>
            <button data-testid="export-source-csv" onClick={exportSourceCSV} className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Export</button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-2.5">
          {sources.map((s) => (
            <button key={s.source} type="button" onClick={() => setSourceFilter(sourceFilter === s.source ? null : s.source)}
              data-testid={`source-row-${s.source.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
              className={`flex items-center gap-3 text-left rounded-md px-1.5 py-1 -mx-1.5 transition-colors hover:bg-slate-50 ${sourceFilter === s.source ? "bg-cyan-50 ring-1 ring-cyan-200" : ""}`}>
              <div className="w-36 truncate text-sm font-medium text-slate-700" title={s.source}>{s.source}</div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(s.count / maxCount) * 100}%` }} /></div>
              <div className="w-8 text-right font-mono-metric text-sm font-bold text-slate-900">{s.count}</div>
              <div className="w-28 text-right text-xs text-emerald-600">{s.converted} conv{s.value ? ` · ${fmtCurrency(s.value, true)}` : ""}</div>
            </button>
          ))}
          {sources.length === 0 && <p className="text-sm text-slate-400">No leads in this period.</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 kanban-scroll">
        {STAGES.map((stage) => {
          const items = boardLeads.filter((l) => l.stage === stage.id);
          const value = items.reduce((s, l) => s + (l.target_value || 0), 0);
          return (
            <div key={stage.id} data-testid={`kanban-column-${stage.id.replace("_", "-")}`}
              className={`bg-slate-50/80 rounded-xl border border-slate-200 border-t-[3px] ${stage.color} p-3 min-h-[200px]`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{stage.label}</span>
                <span className="text-[11px] font-semibold bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-500">{items.length}</span>
              </div>
              <div className="text-[10px] font-mono-metric text-slate-400 mb-2 px-1">{fmtCurrency(value, true)}</div>
              <div className="space-y-2.5">
                {items.map((l) => <LeadCard key={l.id} lead={l} onMove={(lead, s) => move.mutate({ lead, stage: s })} onWelcome={(lead) => welcome.mutate(lead)} />)}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Consult Booking Link</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Booking URL</Label>
            <Input data-testid="booking-url-input" value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://themensclinic.com/book" />
            <p className="text-xs text-slate-400">Included in welcome texts so leads can self-schedule in one tap.</p>
          </div>
          <DialogFooter>
            <Button data-testid="booking-save-button" onClick={() => saveBooking.mutate(bookingUrl.trim())} disabled={saveBooking.isPending} className="w-full bg-slate-900 hover:bg-slate-800 text-white">{saveBooking.isPending ? "Saving…" : "Save booking link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={welcomeAllOpen} onOpenChange={setWelcomeAllOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Text all new leads</DialogTitle></DialogHeader>
          <div className="py-2 text-sm text-slate-600">
            This will send a welcome text to <span className="font-bold text-slate-900">{newToText.length}</span> new lead{newToText.length === 1 ? "" : "s"}{sourceFilter ? ` from ${sourceFilter}` : ""} ({rangeLabel}) and move them to Contacted. Opted-out leads are skipped.
            <div className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">These are live SMS messages sent to real people.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWelcomeAllOpen(false)}>Cancel</Button>
            <Button data-testid="welcome-all-confirm" disabled={newToText.length === 0 || welcomeAll.isPending} onClick={() => welcomeAll.mutate(newToText.map((l) => l.id))} className="bg-cyan-600 hover:bg-cyan-700 text-white">{welcomeAll.isPending ? "Sending…" : `Send ${newToText.length} text${newToText.length === 1 ? "" : "s"}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
